import { Injectable, inject } from '@angular/core';
import { HotToastService, ToastOptions, CreateHotToastRef } from '@ngxpert/hot-toast';
import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';

import { FormattedError } from '../http/formatted-error';
import { SoundsService } from '../sounds';
import { isMobile } from '../helpers';

import { ConfirmComponent } from './confirm.component';
import { CustomNotificationComponent } from './custom-notification/custom-notification.component';

const defaultToastOptions: ToastOptions<any> = {
  position: isMobile ? 'top-center' : 'bottom-right',
  dismissible: true
};

type NotificationMessage = FormattedError|string;

@Injectable({
  providedIn: 'root'
})
export class Notifier {
  private readonly toastService = inject(HotToastService);
  private readonly soundsService = inject(SoundsService);

  success(message: NotificationMessage, toastOptions: ToastOptions<any> = {}): CreateHotToastRef<any> {
    this.soundsService.playSound('success-notify');

    toastOptions = {
      ...toastOptions,
      className: 'hot-toast_success',
      iconTheme: {
        primary: '#75D690'
      }
    };

    if(message instanceof FormattedError) {
      message = message.format();
    }

    return this.toastService.success(message, {
      ...defaultToastOptions,
      ...toastOptions
    });
  }

  error(message: NotificationMessage, toastOptions: ToastOptions<any> = {}): CreateHotToastRef<any> {
    this.soundsService.playSound('error-notify');

    toastOptions = {
      ...toastOptions,
      className: 'hot-toast_error',
      iconTheme: {
        primary: '#FA8181',
        secondary: '#736769'
      }
    };

    if(message instanceof FormattedError) {
      message = message.format();
    }

    return this.toastService.error(message, {
      ...defaultToastOptions,
      ...toastOptions
    });
  }

  warning(message: NotificationMessage, toastOptions: ToastOptions<any> = {}): CreateHotToastRef<any> {
    toastOptions = {
      ...toastOptions,
      className: 'hot-toast_warning',
      iconTheme: {
        primary: '#FDCA7D',
        secondary: '#736769'
      }
    };

    if(message instanceof FormattedError) {
      message = message.format();
    }

    return this.toastService.warning(message, {
      ...defaultToastOptions,
      ...toastOptions
    });
  }

  loading(message: NotificationMessage, toastOptions: ToastOptions<any> = {}): CreateHotToastRef<any> {
    toastOptions = {
      ...toastOptions,
      className: 'hot-toast_loading'
    };
    
    if(message instanceof FormattedError) {
      message = message.format();
    }

    return this.toastService.loading(message, {
      ...defaultToastOptions,
      ...toastOptions
    });
  }

  show(component: any, toastOptions: ToastOptions<any> = {}): CreateHotToastRef<any> {
    return this.toastService.show(component, {
      ...defaultToastOptions,
      ...toastOptions
    });
  }

  confirm(component: any = ConfirmComponent): Observable<boolean> {
    const confirm = new Subject<boolean>();

    this.warning(component, {
      data: confirm,
      className: 'hot-toast-bar-base_confirm',
      autoClose: false,
      dismissible: false,
      position: 'bottom-center'
    });

    return confirm.pipe( take(1) );
  }

  observe(
    loading: string = $localize`:@@system.loading:Загрузка`,
    success: string = $localize`:@@system.success:Успех!`,
    error: string = $localize`:@@system.error:Возникла ошибка`
  ) {
    return this.toastService.observe({
      loading,
      success,
      error
    });
  }

  cardError(message: NotificationMessage, toastOptions: ToastOptions<any> = {}): CreateHotToastRef<any> {
    this.soundsService.playSound('error-notify');

    toastOptions = {
      ...toastOptions,
      className: 'hot-toast_error_new',
      dismissible: false
    };

    if(message instanceof FormattedError) {
      message = message.format();
    }

    return this.toastService.show(CustomNotificationComponent, {
      data: { message: message.toString() },
      ...defaultToastOptions,
      ...toastOptions
    });
  }
}